import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { openPaymentGateway } from "../../services/cyberGatewayService";
import { checkRenewalPaymentStatus, getMyRenewalShroffVoucherPdf } from "../../services/renewalService";

// Opens a blob in a pre-opened tab (must be window.open'd synchronously
// inside the triggering click to avoid the popup blocker) — same pattern as
// ClubRenewalPaymentModal.jsx / CoachDashboard.jsx's own openBlobInTab.
function openBlobInTab(blob, tab) {
  const url = URL.createObjectURL(blob);
  if (tab && !tab.closed) tab.location.href = url;
  else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const buildName = (initials, denoted, lastname) => {
  const lead = String(initials || denoted || "").trim();
  const tail = String(lastname || "").trim();
  return [lead, tail].filter(Boolean).join(" ") || "—";
};

// One label/value row in the success card's details list — duplicated from
// PaymentSuccess.jsx / ClubRenewalPaymentModal.jsx per this codebase's "each
// call site owns its own JSX independently" convention (see CLAUDE.md), so
// the membership renewal success state reads identically to those. Renders
// nothing if `value` is empty.
const DetailRow = ({ icon, label, value, mono, delayMs }) => {
  if (!value) return null;
  return (
    <div
      className="motion-safe:animate-fade-in-up flex items-center gap-3 py-3 first:pt-0 last:pb-0"
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
        {icon}
      </span>
      <div className="min-w-0 flex-1 flex items-center justify-between gap-3">
        <span className="text-xs sm:text-sm text-gray-500">{label}</span>
        <span
          className={`text-sm sm:text-base font-semibold text-gray-800 truncate max-w-[60%] text-right ${
            mono ? "font-mono tracking-tight" : ""
          }`}
        >
          {value}
        </span>
      </div>
    </div>
  );
};

// "Pay for your membership renewal" — mirrors ClubRenewalPaymentModal.jsx
// (itself mirroring IdCardReprintPaymentModal.jsx) for individual membership
// renewals: card/counter selection + gateway-popup + polling UX, driven by
// the payment_payload MembershipRenewalController::formatRenewal() returns
// on every pending renewal (store/show/myRenewals alike — see
// renewalService.js). Like club renewals, there's no `payment_options`
// array distinguishing card vs shroff — a renewal either has a
// payment_payload (card is available) or it doesn't, and shroff/counter is
// always offered via the existing voucher route.
//
// Rendered as a nested sub-page inside the "Renewal" tab's own content area
// (MemberDashboard.jsx swaps it in for RenewalView the same way
// BookingDetailsPage replaces the active tab for viewingBookingId, and the
// same way ClubRenewalPaymentModal.jsx is swapped into CoachDashboard.jsx's
// Renew Club tab) — not a modal/overlay and not a route navigation, so the
// sidebar stays put, "Renewal" stays the highlighted nav item, and an
// already-logged-in member never gets yanked out of the dashboard.
//
// Polls the dedicated /renewal-payments/status route via
// checkRenewalPaymentStatus. That route (like store/show/myRenewals) never
// joins the payment_status category description, so `payment_status` on the
// response is always null — completion is detected via `paid_at` instead
// (set the moment MembershipRenewalController::completeRenewal() runs).
//
// Props: `renewal` (a row from getMyRenewals()/requestRenewal(), must have
// payment_reference + payable_amount; payment_payload required for the card
// option to show), `member` (optional — the caller's already-loaded member
// object, used only to show the member's name/status on the summary card;
// renewal rows carry no name field, only member_code), `onClose()`,
// `onPaid(updatedRenewal)` — called once payment_status is confirmed paid;
// the caller owns its own refresh (renewals list, member profile) same as
// other modals in this app.
export default function MembershipRenewalPaymentModal({ renewal, member, onClose, onPaid }) {
  const paymentPayload = renewal.payment_payload || null;
  const amount = renewal.payable_amount != null ? Number(renewal.payable_amount) : null;
  const amountLabel = amount != null ? `LKR ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—";
  const referenceNumber = paymentPayload?.reference2 || renewal.external_payment_reference || renewal.payment_reference;
  const memberName = member ? buildName(member.initials, member.name_denoted_by_initials, member.lastname) : "—";
  const memberCode = renewal.member_code || member?.member_code || "—";
  const currentStatus = member?.member_status || "Expired";

  const [selected, setSelected] = useState(paymentPayload ? "card" : "counter");
  const [gatewayOpened, setGatewayOpened] = useState(0);
  const [error, setError] = useState("");
  const popupRef = useRef(null);
  const [verifying, setVerifying] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState("");
  const [counterConfirmed, setCounterConfirmed] = useState(false);
  const [paidResult, setPaidResult] = useState(null);
  const [downloadingVoucher, setDownloadingVoucher] = useState(false);

  const statusParams = { renewal_id: renewal.renewal_id, payment_reference: renewal.payment_reference };

  const verifyPayment = async () => {
    setVerifying(true);
    setPaymentNotice("");
    try {
      const res = await checkRenewalPaymentStatus(statusParams);
      if (res?.paid_at) {
        setPaidResult(res);
        onPaid?.(res);
        return;
      }
      setPaymentNotice("Payment is still pending. If you completed the payment, please wait a moment and check again.");
    } catch {
      setPaymentNotice("Could not verify payment status. Please check again.");
    } finally {
      setVerifying(false);
    }
  };

  // While the gateway popup is open, poll for the real payment status every
  // few seconds — same shape as ClubRenewalPaymentModal.jsx/PaymentMethod.jsx.
  useEffect(() => {
    if (!gatewayOpened) return undefined;
    const popup = popupRef.current;
    if (!popup) return undefined;

    let stopped = false;
    let inFlight = false;

    const stopPolling = () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };

    const tick = async () => {
      if (stopped || inFlight) return;
      if (popup.closed) {
        stopPolling();
        verifyPayment();
        return;
      }
      inFlight = true;
      try {
        const res = await checkRenewalPaymentStatus(statusParams);
        if (stopped) return;
        if (res?.paid_at) {
          stopPolling();
          popup.close();
          setPaidResult(res);
          onPaid?.(res);
        }
      } catch {
        // Transient error — keep polling.
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(tick, 3000);
    const onFocusOrVisible = () => {
      if (document.visibilityState === "hidden") return;
      tick();
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);

    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayOpened]);

  const handleDownloadVoucher = async () => {
    if (downloadingVoucher) return;
    const tab = window.open("", "_blank");
    setDownloadingVoucher(true);
    try {
      const blob = await getMyRenewalShroffVoucherPdf(renewal.renewal_id);
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(err?.response?.data?.message || "Could not load the voucher.");
    } finally {
      setDownloadingVoucher(false);
    }
  };

  const handleContinue = () => {
    setError("");
    setPaymentNotice("");

    if (selected === "counter") {
      setCounterConfirmed(true);
      return;
    }

    if (!paymentPayload) {
      setError("Missing payment details. Please refresh and try again.");
      return;
    }

    const popup = openPaymentGateway(paymentPayload);
    if (!popup) {
      popupRef.current = null;
      setError("Your browser blocked the payment window. Please allow popups for this site and try again.");
      return;
    }
    popupRef.current = popup;
    setGatewayOpened((n) => n + 1);
  };

  const confirmingPayment = selected === "card" && gatewayOpened > 0 && !paymentNotice && !paidResult;
  const cardSelected = selected === "card";
  const counterSelected = selected === "counter";

  // ── "Back to Renewal" — same shape as CoachDashboard.jsx's own
  // BackToBookings / ClubRenewalPaymentModal.jsx's BackToRenewClub, so
  // returning here reads the same as any other nested sub-page in this
  // dashboard.
  const BackToRenewal = () => (
    <button
      type="button"
      onClick={onClose}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-white shadow-sm hover:bg-gray-50 text-[#0f1c3f]"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
      Back to Renewal
    </button>
  );

  // ── Transient "verifying" state ─────────────────────────────────────────
  if (verifying) {
    return (
      <div className="space-y-4">
        <BackToRenewal />
        <div className="bg-white rounded-2xl shadow-sm p-10 flex flex-col items-center gap-3 text-center">
          <svg className="animate-spin w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-sm font-semibold text-gray-700">Verifying your payment…</p>
          <p className="text-xs text-gray-400">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  // ── "Payment Successful" state — same checkmark animation, card layout and
  // detail-row design as PaymentSuccess.jsx (the registration flow's success
  // page) / ClubRenewalPaymentModal.jsx, kept as an embedded card here
  // instead of a full route navigation so the member stays on the Renewal
  // sub-page (see file-top note).
  if (paidResult) {
    const paidAmount = paidResult.paid_amount ?? paidResult.payable_amount ?? amount;
    const paidAmountLabel = paidAmount != null ? `LKR ${Number(paidAmount).toLocaleString()}` : amountLabel;
    const paidReference = paidResult.external_payment_reference || paidResult.payment_reference || referenceNumber;
    const paidName = memberName !== "—" ? memberName : paidResult.member_code || memberCode;

    return (
      <div className="max-w-md sm:max-w-lg mx-auto">
        <div className="motion-safe:animate-scale-in bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 p-6 sm:p-9 md:p-10 text-center">
          {/* Success icon — glow ring behind an animated circle + checkmark draw-in */}
          <div className="relative mx-auto mb-5 sm:mb-6 w-20 h-20 sm:w-24 sm:h-24">
            <span className="absolute inset-0 rounded-full bg-green-400/20 motion-safe:animate-ping [animation-iteration-count:1] [animation-duration:0.9s]" />
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-green-50 to-emerald-100" />
            <svg viewBox="0 0 64 64" className="relative w-full h-full">
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="#16a34a"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="176"
                className="motion-safe:animate-draw-circle"
              />
              <path
                d="M19 33 L27.5 41.5 L45 22"
                fill="none"
                stroke="#16a34a"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="48"
                className="motion-safe:animate-draw-check"
              />
            </svg>
          </div>

          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Payment Successful</h2>
          <p className="motion-safe:animate-fade-in-up text-sm text-gray-500 mt-2" style={{ animationDelay: "40ms" }}>
            Your membership has been renewed.
          </p>

          <div className="motion-safe:animate-fade-in-up bg-gray-50/80 rounded-2xl px-4 sm:px-5 py-1 mt-6 sm:mt-7 text-left divide-y divide-gray-100">
            <DetailRow
              delayMs={80}
              label="Payer"
              value={paidName}
              icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              }
            />
            <DetailRow
              delayMs={140}
              label="Amount paid"
              value={paidAmountLabel}
              icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.66 0-3 .9-3 2s1.34 2 3 2 3 .9 3 2-1.34 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V6m0 10v2m9-6a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
            <DetailRow
              delayMs={200}
              label="Reference"
              value={paidReference}
              mono
              icon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
            />
          </div>

          <button
            onClick={onClose}
            className="motion-safe:animate-fade-in-up group w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 active:from-blue-900 active:to-blue-950 text-white font-semibold py-3.5 rounded-xl text-sm sm:text-base mt-7 sm:mt-8 transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5"
            style={{ animationDelay: "320ms" }}
          >
            Done
            <svg
              className="w-4 h-4 transition-transform group-hover:translate-x-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // ── "Pay at Counter" confirmation state — reference number + instructions ──
  if (counterConfirmed) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setCounterConfirmed(false)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-white shadow-sm hover:bg-gray-50 text-[#0f1c3f]"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-9 text-center max-w-xl mx-auto">
          <div className="mx-auto w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m6-14h.01M9 11h.01M9 15h.01M14 7h.01M14 11h.01M14 15h.01" />
            </svg>
          </div>
          <h2 className="text-xl font-extrabold text-gray-900">Pay at Counter</h2>
          <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
            Take this reference to the counter to pay in person and renew your membership.
          </p>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100/60 rounded-xl px-5 py-5 mt-6 text-center">
            <p className="text-xs font-medium text-gray-500">Reference Number</p>
            <p className="text-2xl sm:text-3xl font-extrabold text-blue-800 mt-1 tracking-wide font-mono break-all">
              {referenceNumber || "—"}
            </p>
            <p className="text-xs text-gray-500 mt-2">Amount due: <span className="font-semibold text-gray-700">{amountLabel}</span></p>
          </div>

          <button
            onClick={handleDownloadVoucher}
            disabled={downloadingVoucher}
            className="w-full inline-flex items-center justify-center gap-2 border-2 border-blue-700 text-blue-700 hover:bg-blue-50 font-semibold py-3 rounded-xl text-sm mt-4 transition disabled:opacity-60"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            {downloadingVoucher ? "Opening…" : "Download Voucher"}
          </button>

          <button
            onClick={onClose}
            className="w-full bg-blue-700 hover:bg-blue-800 active:bg-blue-900 text-white font-semibold py-3 rounded-xl text-sm mt-3 transition shadow-sm hover:shadow-md"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // ── Main "Select Payment Method" sub-page ───────────────────────────────
  return (
    <div className="space-y-5">
      <BackToRenewal />

      {/* Title */}
      <div className="text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-3">
          <svg className="w-7 h-7 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
          </svg>
        </div>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Select Payment Method</h2>
        <p className="text-sm text-gray-500 mt-1">Choose how you would like to complete your membership renewal payment.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Left: payment method selection */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-7">
          <h3 className="text-base font-bold text-gray-900">Payment Method</h3>
          <p className="text-sm text-gray-500 mt-0.5 mb-5">Select your preferred payment method to proceed with the renewal.</p>

          {/* Card Payment option */}
          {paymentPayload && (
            <label
              className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-4 cursor-pointer transition ${
                cardSelected ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="membershipRenewalPaymentMethod"
                className="sr-only"
                checked={cardSelected}
                onChange={() => setSelected("card")}
              />
              <span
                className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  cardSelected ? "border-blue-600" : "border-gray-300"
                }`}
              >
                {cardSelected && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
              </span>
              <span className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                </svg>
              </span>
              <span className="flex-1 min-w-0">
                <span className="text-sm font-bold text-gray-900">Card Payment</span>
                <span className="block text-xs text-gray-500 mt-0.5">Pay securely using your debit or credit card</span>
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 flex-shrink-0">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secure &amp; Encrypted
              </span>
            </label>
          )}

          {/* Pay at Counter option */}
          <label
            className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-4 cursor-pointer transition mt-3 ${
              counterSelected ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <input
              type="radio"
              name="membershipRenewalPaymentMethod"
              className="sr-only"
              checked={counterSelected}
              onChange={() => setSelected("counter")}
            />
            <span
              className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                counterSelected ? "border-blue-600" : "border-gray-300"
              }`}
            >
              {counterSelected && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
            </span>
            <span className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m6-14h.01M9 11h.01M9 15h.01M14 7h.01M14 11h.01M14 15h.01" />
              </svg>
            </span>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-bold text-gray-900">Pay at Counter</span>
              <span className="block text-xs text-gray-500 mt-0.5">Pay at the counter in person.</span>
            </span>
          </label>

          {error && <p className="text-sm text-red-500 text-center mt-4">{error}</p>}
          {paymentNotice && <p className="text-sm text-amber-600 text-center mt-4">{paymentNotice}</p>}

          <div className="mt-6">
            <button
              onClick={handleContinue}
              disabled={confirmingPayment}
              className="w-full bg-blue-700 hover:bg-blue-800 active:bg-blue-900 text-white font-semibold py-3 rounded-xl text-sm transition shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:bg-blue-700"
            >
              <span className="flex items-center justify-center gap-2">
                {confirmingPayment ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Confirming Payment…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                    </svg>
                    {cardSelected && gatewayOpened ? "Reopen Payment Window" : "Proceed to Pay"}
                  </>
                )}
              </span>
            </button>

            {confirmingPayment && (
              <p className="text-xs font-medium text-amber-600 text-center mt-3">Confirming your payment… please don't close this.</p>
            )}

            {paymentNotice && (
              <button
                onClick={verifyPayment}
                className="w-full text-sm font-medium text-blue-700 hover:text-blue-800 border border-blue-100 hover:bg-blue-50 rounded-xl py-2.5 mt-3 transition"
              >
                Check Payment Status Again
              </button>
            )}

            <div className="flex items-center gap-3 my-5">
              <span className="flex-1 h-px bg-gray-100" />
              <span className="text-xs font-medium text-gray-400">OR</span>
              <span className="flex-1 h-px bg-gray-100" />
            </div>

            <button
              onClick={onClose}
              className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl py-3 transition"
            >
              Cancel Renewal
            </button>
          </div>
        </div>

        {/* Right: renewal summary */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-7">
          <div className="flex items-center gap-2.5 mb-5">
            <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </span>
            <h3 className="text-base font-bold text-gray-900">Renewal Summary</h3>
          </div>

          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-gray-500">Member Name</dt>
              <dd className="font-medium text-gray-800 text-right truncate">{memberName}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-gray-500">Member Code</dt>
              <dd className="font-medium text-gray-800 text-right truncate">{memberCode}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-gray-500">Renewal Type</dt>
              <dd className="font-medium text-gray-800 text-right truncate">Membership Renewal</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-gray-500">Current Status</dt>
              <dd className="text-right">
                <span className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-600">
                  {currentStatus}
                </span>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-gray-500">Renewal Fee</dt>
              <dd className="font-medium text-gray-800 text-right">{amountLabel}</dd>
            </div>
          </dl>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100/60 rounded-xl px-4 py-4 mt-5">
            <p className="text-xs font-medium text-gray-500">Total Amount</p>
            <p className="text-2xl font-extrabold text-blue-800 mt-0.5">{amountLabel}</p>
          </div>

          <div className="flex items-start gap-2.5 rounded-xl bg-green-50 px-4 py-3.5 mt-4">
            <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>
              <span className="block text-xs font-semibold text-green-700">Secure Payment</span>
              <span className="block text-xs text-green-700/80 mt-0.5">Your payment details are protected by industry-standard encryption.</span>
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-5">
            <span className="select-none font-bold italic tracking-tight text-[15px] leading-none" style={{ color: "#1a1f71" }}>
              VISA
            </span>
            <span className="text-gray-300">|</span>
            <span className="inline-flex items-center">
              <span className="w-5 h-5 rounded-full" style={{ backgroundColor: "#eb001b", marginRight: "-8px" }} />
              <span className="w-5 h-5 rounded-full" style={{ backgroundColor: "#f79e1b", opacity: 0.9 }} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
